/**
 * reminderMessageDefaults — the 18 default INLINE reminder messages and the pure helpers around the
 * `inline:<slotKey>` sentinel (frontend ESM port). Import-free on purpose: node-tests read this exact file.
 *
 * CANONICAL SOURCE: teamio-lambda-layer/dependencies_source_for_layer/reminderMessageDefaults.js
 * (deployed to /opt/reminderMessageDefaults). The body between the SHARED-BODY markers must stay
 * byte-identical to the layer copy — cypress/node-tests/reminder-message-defaults.test.js fails if they
 * drift. Rationale, the sentinel contract and the validation rule live in the layer copy's header and in
 * docs/plans/2026-10-17-inline-reminder-messages-plan.md; the behaviour is pinned by
 * teamio-lambda-layer/__tests__/reminderMessageDefaults.test.js.
 *
 * ESM here / CJS there: webpack can't run CommonJS under src/ (runtime crash) and node:test can't import ESM
 * without "type":"module" — so the two export styles differ while the body stays shared.
 *
 * ONLINE-SCHEDULING LINK: the four recall slots and the recall-type rules may hold the token
 * {{online_scheduling_link}} (resolved at send time by teamio-utility). It is NOT a chip and NOT in
 * templateVariables.js KNOWN_TEMPLATE_VARIABLES - the recall slot entries carry `bookingLink: true` instead.
 * Plan: docs/plans/2026-10-19-online-scheduling-link-token-plan.md.
 */

// === SHARED-BODY-START === (everything between these markers must be byte-identical
// to teamio-frontend/src/utils/reminderMessageDefaults.js — a frontend node-test enforces it)
const INLINE_PREFIX = 'inline:';

// A per-recall-type rule's inline template id: 'inline:rule:<ruleId>:sms|email' (<= 34 chars, under the
// varchar(45) log column). The rule id is made by the screen: [a-z0-9]{6,16}. Every cloud reader matches a rule
// by EQUALITY of this id, so each rule template needs its own.
const RULE_ID_RE = /^[a-z0-9]{6,16}$/;
const RULE_INLINE_ID_RE = /^inline:rule:([a-z0-9]{6,16}):(sms|email)$/;
// A rule's message starts from the recall SINGLE default of its channel.
const RULE_CHANNEL_SLOT = Object.freeze({
  sms: 'recallReminderTplForSingleApt',
  email: 'recallReminderEmailTplForSingleApt',
});

// The online-scheduling link (plan teamio-frontend/docs/plans/2026-10-19-online-scheduling-link-token-plan.md). The
// token sits in a RECALL message (the four recall slots + the recall-type rules); teamio-utility swaps it for a short
// link to the site's booking page at send time. It is deliberately NOT a chip and NOT in KNOWN_TEMPLATE_VARIABLES
// (plan 3.2): the recall slot entries carry `bookingLink: true` instead, and only the settings screen's gated chip
// reads that flag.
const BOOKING_LINK_VARIABLE = 'online_scheduling_link';
const BOOKING_LINK_TOKEN = '{{online_scheduling_link}}';
const BOOKING_PAGE_PATH = '/online-scheduling/book-appointment.html';
// The tail of the INLINE_BOOKING_LINK_IN_SUBJECT message: '<label>' + this (a subject line cannot carry the link).
const BOOKING_LINK_IN_SUBJECT_TAIL = ": the online scheduling link can't go in the subject line — put it in the message.";
// Tolerates spaces inside the braces ('{{ online_scheduling_link }}'). BOOKING_LINK_RE is for .test(); the global
// BOOKING_LINK_RE_ALL only ever goes to String.replace, so no regex lastIndex state is shared between calls.
const BOOKING_LINK_RE = /\{\{\s*online_scheduling_link\s*\}\}/;
const BOOKING_LINK_RE_ALL = /\{\{\s*online_scheduling_link\s*\}\}/g;

// Variable chips each message type offers (names without braces). Every {{variable}} a default's `text` uses must be
// in its entry's chips, so the office can always re-insert what the default contains. The one exception is the
// booking-link token in `textWithBookingLink` (see BOOKING_LINK_TOKEN): it has no chip here, by design.
const CHIPS_SINGLE = Object.freeze(['patient_first_name', 'apt_date', 'apt_dayofweek', 'apt_provider', 'site_name', 'site_officephone']);
const CHIPS_FAMILY = Object.freeze(['guarantor_first_name', 'family_appts', 'apt_date', 'apt_dayofweek', 'site_name', 'site_officephone']);
const CHIPS_AUTO = Object.freeze(['patient_first_name', 'apt_date', 'apt_dayofweek', 'site_name']);
const CHIPS_PREMED = Object.freeze(['patient_first_name', 'apt_date', 'apt_dayofweek', 'site_name', 'site_officephone']);
const CHIPS_BOOKED = Object.freeze(CHIPS_SINGLE.concat(['online_apttype']));
const CHIPS_RECALL_SINGLE = Object.freeze(['patient_first_name', 'recall_due_date', 'site_name', 'site_officephone']);
const CHIPS_RECALL_FAMILY = Object.freeze(['guarantor_first_name', 'minor_names', 'site_name', 'site_officephone']);
// Email confirmation bodies also offer the confirm button; the signature block is never a chip.
const CHIPS_CONFIRM_SINGLE = Object.freeze(CHIPS_SINGLE.concat(['confirm_button']));
const CHIPS_CONFIRM_FAMILY = Object.freeze(CHIPS_FAMILY.concat(['confirm_button']));

// Order = SLOT_SPECS in teamio-admin/seed-apt-reminder-template-slots.js = SLOT_KEYS in
// AptReminderSettings.vue _refreshTemplateSlots. `subject` exists on email entries only; `helper` is an
// optional one-line hint the UI shows under that one box (no default uses one right now). `bookingLink: true` marks
// the four recall slots whose message may carry the online-scheduling link token; `textWithBookingLink` (recall
// single text only) is the default for an office that HAS online scheduling (pickDefaultText) - `text` is the phone
// version that is safe for every office.
const SLOT_DEFAULTS = Object.freeze([
  {
    key: 'textConfirmTplForSingleApt',
    channel: 'sms',
    family: 'apt-reminder',
    label: 'Text confirmations – single appointment',
    text: '{{site_name}}: Hi {{patient_first_name}}, you have an appointment coming up on {{apt_date}}. Please respond with C to confirm this appointment.',
    chips: CHIPS_SINGLE,
  },
  {
    key: 'textConfirmTplForMultiApt',
    channel: 'sms',
    family: 'apt-reminder',
    label: 'Text confirmations – family appointments',
    text: '{{site_name}}: Hi {{guarantor_first_name}}.\nYou have the following family appointments:\n{{family_appts}}\nReply with y or c to confirm or n to cancel.',
    chips: CHIPS_FAMILY,
  },
  {
    key: 'textConfirmTplForAutoReply',
    channel: 'sms',
    family: 'apt-reminder',
    label: 'Text confirmations – auto-reply',
    text: 'Thank you for confirming your appointment. We look forward to seeing you!',
    chips: CHIPS_AUTO,
  },
  {
    key: 'textReminderTplForSingleApt',
    channel: 'sms',
    family: 'apt-reminder',
    label: 'Text reminders – single appointment',
    text: '{{site_name}}: Hello {{patient_first_name}}, this is a reminder of your appointment on {{apt_date}}.',
    chips: CHIPS_SINGLE,
  },
  {
    key: 'textReminderTplForMultiApt',
    channel: 'sms',
    family: 'apt-reminder',
    label: 'Text reminders – family appointments',
    text: '{{site_name}}: Hi {{guarantor_first_name}},\nThis is a reminder for your appointment(s) on {{apt_date}} with {{site_name}}:\n{{family_appts}}',
    chips: CHIPS_FAMILY,
  },
  {
    key: 'textSameDayReminderTplForSingleApt',
    channel: 'sms',
    family: 'apt-reminder',
    label: 'Same-day text reminder – single appointment',
    text: '{{site_name}}: Hello {{patient_first_name}}, this is a reminder of your appointment today on {{apt_date}}.',
    chips: CHIPS_SINGLE,
  },
  {
    key: 'textSameDayReminderTplForMultiApt',
    channel: 'sms',
    family: 'apt-reminder',
    label: 'Same-day text reminder – family appointments',
    text: '{{site_name}}: Hi {{guarantor_first_name}}\nThis is a reminder for your family appointments today with {{site_name}}:\n{{family_appts}}',
    chips: CHIPS_FAMILY,
  },
  {
    key: 'emailConfirmationTplForSingleApt',
    channel: 'email',
    family: 'apt-reminder',
    label: 'Email confirmations – single appointment',
    subject: 'Dental Appointment Confirmation',
    text: "Hi {{patient_first_name}}!\n\nYou have an appointment on {{apt_date}} with {{site_name}}.\n\nIf you've already confirmed via text message, you can ignore this email.\n\nPlease click the button below to confirm your appointment.\n{{confirm_button}}",
    chips: CHIPS_CONFIRM_SINGLE,
  },
  {
    key: 'emailConfirmationTplForMultiApt',
    channel: 'email',
    family: 'apt-reminder',
    label: 'Email confirmations – family appointments',
    subject: 'Dental Appointment Confirmation for Family',
    text: "Hi {{guarantor_first_name}},\nYou have the following family appointments with {{site_name}}:\n{{family_appts}}\nIf you've already confirmed via text message, you can ignore this email.\nOtherwise, please click the button below to confirm your appointment.\n{{confirm_button}}\n\nThanks",
    chips: CHIPS_CONFIRM_FAMILY,
  },
  {
    key: 'emailReminderTplForSingleApt',
    channel: 'email',
    family: 'apt-reminder',
    label: 'Email reminders – single appointment',
    subject: 'Dental Appointment Reminder',
    text: 'Hi {{patient_first_name}},\n\nThis is a reminder for your appointment on {{apt_date}} with {{site_name}}.\n\nThanks!',
    chips: CHIPS_SINGLE,
  },
  {
    key: 'emailReminderTplForMultiApt',
    channel: 'email',
    family: 'apt-reminder',
    label: 'Email reminders – family appointments',
    subject: 'Dental Appointment reminder for Family',
    text: 'Hi {{guarantor_first_name}},\nThis is a reminder for your family appointments with {{site_name}}:\n{{family_appts}}\n\nThanks!',
    chips: CHIPS_FAMILY,
  },
  {
    key: 'premedTpl',
    channel: 'sms',
    family: 'apt-reminder',
    label: 'Pre-med reminder',
    text: "{{site_name}}: Hello {{patient_first_name}}, please don't forget to premedicate 1 hour prior to your appointment on {{apt_date}}.",
    chips: CHIPS_PREMED,
  },
  {
    key: 'apptBookedSmsTpl',
    channel: 'sms',
    family: 'apt-booked',
    label: 'Appointment booked – text',
    text: '{{site_name}}: Hello {{patient_first_name}}, your appointment is scheduled for {{apt_date}}. If you have any questions, please call our office at {{site_officephone}}.',
    chips: CHIPS_BOOKED,
  },
  {
    key: 'apptBookedEmailTpl',
    channel: 'email',
    family: 'apt-booked',
    label: 'Appointment booked – email',
    subject: 'Your Dental Appointment',
    text: 'Hello {{patient_first_name}}, your appointment is scheduled for {{apt_date}}.\n\nIf you have any questions, please call our office at {{site_officephone}}.\n\nThank you!',
    chips: CHIPS_BOOKED,
  },
  {
    key: 'recallReminderTplForSingleApt',
    channel: 'sms',
    family: 'recall-reminder',
    label: 'Recall text – single patient',
    text: '{{site_name}}: Hello {{patient_first_name}}, you are due for your periodic exam and cleaning visit. Please reply to this message or call us at {{site_officephone}} to schedule.',
    textWithBookingLink: '{{site_name}}: Hello {{patient_first_name}}, you are due for your periodic exam and cleaning visit. Please reply to this message or book online here: {{online_scheduling_link}}',
    chips: CHIPS_RECALL_SINGLE,
    bookingLink: true,
  },
  {
    key: 'recallReminderTplForMultiApt',
    channel: 'sms',
    family: 'recall-reminder',
    label: 'Recall text – family',
    text: '{{site_name}}: Hi {{guarantor_first_name}}!\nThe following family members are due for their periodic exam and cleaning appointment.\n{{minor_names}}\nPlease reply to this message or call us at {{site_officephone}} to schedule your appointment(s).',
    chips: CHIPS_RECALL_FAMILY,
    bookingLink: true,
  },
  {
    key: 'recallReminderEmailTplForSingleApt',
    channel: 'email',
    family: 'recall-reminder',
    label: 'Recall email – single patient',
    subject: 'Time for checkup',
    text: "Hi {{patient_first_name}},\nOur records show that you're due for an appointment for periodic exam and cleaning.\n\nPlease call us at {{site_officephone}} to schedule your appointment!",
    chips: CHIPS_RECALL_SINGLE,
    bookingLink: true,
  },
  {
    key: 'recallReminderEmailTplForMultiApt',
    channel: 'email',
    family: 'recall-reminder',
    label: 'Recall email – family',
    subject: 'Time for Family Check-ups',
    text: 'Hi {{guarantor_first_name}}!\nThe following family members are overdue for their periodic exam and cleaning appointments with {{site_name}}:\n{{minor_names}}\nPlease call us at {{site_officephone}} to schedule your appointment(s)!',
    chips: CHIPS_RECALL_FAMILY,
    bookingLink: true,
  },
].map(function (entry) { return Object.freeze(entry); }));

const SLOT_BY_KEY = new Map(SLOT_DEFAULTS.map(function (entry) { return [entry.key, entry]; }));

/**
 * The frozen default entry for a slot key, or null when the key is not one of the 18 slots.
 * @param {string} key
 * @returns {object|null}
 */
function getSlotDefault(key) {
  return SLOT_BY_KEY.get(key) || null;
}

const RULE_LABELS = Object.freeze({
  sms: 'Recall reminder by recall type – text message',
  email: 'Recall reminder by recall type – email',
});

function makeRuleDefault(channel) {
  const slot = SLOT_BY_KEY.get(RULE_CHANNEL_SLOT[channel]);
  const def = { channel: channel };
  if (channel === 'email') def.subject = slot.subject;
  def.text = slot.text;
  // The rule inherits the recall single slot's link support: both channels may hold the token, and the SMS rule
  // also inherits the "office has online scheduling" default text (the email slot has none).
  if (slot.textWithBookingLink !== undefined) def.textWithBookingLink = slot.textWithBookingLink;
  if (slot.bookingLink === true) def.bookingLink = true;
  def.chips = slot.chips;
  def.label = RULE_LABELS[channel];
  return Object.freeze(def);
}

const RULE_DEFAULTS = Object.freeze({ sms: makeRuleDefault('sms'), email: makeRuleDefault('email') });

/**
 * The frozen default for a recall-type rule's message in a channel: the recall SINGLE default text (and subject,
 * email only) with the recall-single chips and the label the commlog / errors use, plus `bookingLink: true` (both
 * channels) and `textWithBookingLink` (SMS only). Null for any other channel.
 * @param {'sms'|'email'} channel
 * @returns {{channel:string, subject?:string, text:string, textWithBookingLink?:string, bookingLink?:true, chips:ReadonlyArray<string>, label:string}|null}
 */
function getRuleDefault(channel) {
  return channel === 'sms' || channel === 'email' ? RULE_DEFAULTS[channel] : null;
}

/**
 * The sentinel WikiPageID for a slot: 'inline:' + key. Throws on an unknown key so a typo can never
 * mint an id the exe would happily enqueue.
 * @param {string} key
 * @returns {string}
 */
function inlineSlotId(key) {
  if (!getSlotDefault(key)) throw new Error('Unknown reminder slot key: ' + String(key));
  return INLINE_PREFIX + key;
}

/**
 * True only for a string that starts with 'inline:' (a wiki page UUID never does) and is not a well-formed
 * recall-rule id ('inline:rule:<ruleId>:sms|email' — see isInlineRuleId). An unknown 'inline:' id still counts, so
 * the utility skips it loudly instead of treating it as a wiki page.
 * @param {*} id
 * @returns {boolean}
 */
function isInlineSlotId(id) {
  return typeof id === 'string' && id.indexOf(INLINE_PREFIX) === 0 && !RULE_INLINE_ID_RE.test(id);
}

/**
 * The slot key an inline id names, or null when the id is not inline or names no known slot.
 * @param {*} id
 * @returns {string|null}
 */
function slotKeyFromInlineId(id) {
  if (!isInlineSlotId(id)) return null;
  const key = id.slice(INLINE_PREFIX.length);
  return getSlotDefault(key) ? key : null;
}

/**
 * True when `slot` is a settings-blob slot object whose WikiPageID is an inline id.
 * @param {*} slot
 * @returns {boolean}
 */
function isInlineSlot(slot) {
  return slot !== null && typeof slot === 'object' && !Array.isArray(slot) && isInlineSlotId(slot.WikiPageID);
}

/**
 * The slot object stored in the APPT_REMINDER_SETTINGS blob for an inline message. Carries every key the
 * existing wiki-slot code reads (Name, Tags, SendgridTemplateID, both channel flags) so nothing sees
 * undefined, and exactly the channel flag teamio-exe's templateTypeGuard checks. `text` / `subject`
 * default to the slot default only when undefined; a subject is stored for email slots only.
 * @param {string} key
 * @param {{text?:string, subject?:string}} [opts]
 * @returns {object}
 */
function buildInlineSlot(key, opts) {
  const def = getSlotDefault(key);
  if (!def) throw new Error('Unknown reminder slot key: ' + String(key));
  const o = opts || {};
  const inline = { text: o.text === undefined ? def.text : o.text };
  if (def.channel === 'email') inline.subject = o.subject === undefined ? def.subject : o.subject;
  return {
    WikiPageID: inlineSlotId(key),
    Name: 'Plain message',
    IsSmsTemplate: def.channel === 'sms' ? 1 : 0,
    IsEmailTemplate: def.channel === 'email' ? 1 : 0,
    SendgridTemplateID: null,
    Tags: '',
    inline,
  };
}

function isBlankString(v) {
  return typeof v !== 'string' || v.trim() === '';
}

/**
 * The inline template id for a recall-type rule + channel: 'inline:rule:<ruleId>:sms|email'. Null when the rule id
 * is not [a-z0-9]{6,16} or the channel is not 'sms' / 'email' (never mints a malformed id).
 * @param {string} ruleId
 * @param {'sms'|'email'} channel
 * @returns {string|null}
 */
function inlineRuleId(ruleId, channel) {
  if (typeof ruleId !== 'string' || !RULE_ID_RE.test(ruleId)) return null;
  if (channel !== 'sms' && channel !== 'email') return null;
  return INLINE_PREFIX + 'rule:' + ruleId + ':' + channel;
}

/**
 * True only for a well-formed recall-rule inline id ('inline:rule:<ruleId>:sms|email').
 * @param {*} id
 * @returns {boolean}
 */
function isInlineRuleId(id) {
  return typeof id === 'string' && RULE_INLINE_ID_RE.test(id);
}

/**
 * The rule + channel a rule inline id names, or null when the id is not a rule inline id.
 * @param {*} id
 * @returns {{ruleId:string, channel:'sms'|'email'}|null}
 */
function ruleRefFromInlineId(id) {
  if (typeof id !== 'string') return null;
  const m = RULE_INLINE_ID_RE.exec(id);
  return m ? { ruleId: m[1], channel: m[2] } : null;
}

/**
 * True when `tpl` is a rule template object (smsTemplate / emailTemplate of a recallReminderTypeRules entry)
 * whose WikiPageID is a rule inline id.
 * @param {*} tpl
 * @returns {boolean}
 */
function isInlineRuleTemplate(tpl) {
  return tpl !== null && typeof tpl === 'object' && !Array.isArray(tpl) && isInlineRuleId(tpl.WikiPageID);
}

/**
 * The template object stored on a recall-type rule for an inline message: the same shape as buildInlineSlot
 * (every key the wiki-template code reads, exactly the channel flag the exe checks). `text` / `subject` fall back
 * to the rule default when absent OR blank; a subject is stored for email only. Throws on a bad rule id / channel.
 * @param {string} ruleId
 * @param {'sms'|'email'} channel
 * @param {{text?:string, subject?:string}} [opts]
 * @returns {object}
 */
function buildInlineRuleTemplate(ruleId, channel, opts) {
  const id = inlineRuleId(ruleId, channel);
  if (id === null) throw new Error('Invalid recall rule id or channel: ' + String(ruleId) + ' / ' + String(channel));
  const def = RULE_DEFAULTS[channel];
  const o = opts || {};
  const inline = { text: isBlankString(o.text) ? def.text : o.text };
  if (channel === 'email') inline.subject = isBlankString(o.subject) ? def.subject : o.subject;
  return {
    WikiPageID: id,
    Name: 'Plain message',
    IsSmsTemplate: channel === 'sms' ? 1 : 0,
    IsEmailTemplate: channel === 'email' ? 1 : 0,
    SendgridTemplateID: null,
    Tags: '',
    inline,
  };
}

// ---------------------------------------------------------------------------------------------------
// The online-scheduling link in recall messages (plan 2026-10-19-online-scheduling-link-token-plan.md). Pure and
// dependency-free: the settings screen (which box may use the token, which default to prefill), the save checks
// and teamio-utility's send time (what the token turns into) all call these, so they cannot disagree.
// ---------------------------------------------------------------------------------------------------

/**
 * True only for the four recall slots (text + email, single + family) whose message may carry the
 * online-scheduling link token. False for every other key, including non-strings.
 * @param {*} key a slot key such as 'recallReminderTplForSingleApt'
 * @returns {boolean}
 */
function slotAllowsBookingLink(key) {
  const def = getSlotDefault(key);
  return def !== null && def.bookingLink === true;
}

/**
 * True for the inline id of one of those four recall slots ('inline:<slotKey>') and for any well-formed
 * recall-type rule id ('inline:rule:<ruleId>:sms|email'); false for every other slot, wiki ids and junk.
 * @param {*} id
 * @returns {boolean}
 */
function inlineIdAllowsBookingLink(id) {
  if (isInlineRuleId(id)) return true;
  const key = slotKeyFromInlineId(id);
  return key !== null && slotAllowsBookingLink(key);
}

/**
 * True when `text` holds the online-scheduling link token. Tolerates spaces inside the braces
 * ('{{ online_scheduling_link }}'); a non-string is false.
 * @param {*} text
 * @returns {boolean}
 */
function hasBookingLinkToken(text) {
  return typeof text === 'string' && BOOKING_LINK_RE.test(text);
}

/**
 * Replaces EVERY online-scheduling link token in `text` (same space tolerance as hasBookingLinkToken) with
 * `replacement`, taken literally (a '$&' in a URL is not a replace pattern). A non-string `text` is returned
 * unchanged; a non-string `replacement` throws, so a caller bug can never text "undefined".
 * @param {*} text
 * @param {string} replacement
 * @returns {*}
 */
function substituteBookingLink(text, replacement) {
  if (typeof text !== 'string') return text;
  if (typeof replacement !== 'string') throw new Error('substituteBookingLink needs a string replacement');
  return text.replace(BOOKING_LINK_RE_ALL, function () { return replacement; });
}

/**
 * The default message text to prefill for a slot / rule default: `textWithBookingLink` when the office has online
 * scheduling (opts.hasOnlineScheduling === true) AND the default has a non-blank one, else `text` (the phone
 * version, safe for any office).
 * @param {{text:string, textWithBookingLink?:string}} def a SLOT_DEFAULTS entry or a getRuleDefault result
 * @param {{hasOnlineScheduling?:boolean}} [opts]
 * @returns {string}
 */
function pickDefaultText(def, opts) {
  if (opts && opts.hasOnlineScheduling === true && !isBlankString(def.textWithBookingLink)) return def.textWithBookingLink;
  return def.text;
}

/**
 * The site-relative booking-page URL: '/online-scheduling/book-appointment.html?siteID=<siteID>' plus '&apt=<id>'
 * only when `aptTypeId` is a non-blank string. Identical (for normal ids, which encodeURIComponent leaves alone) to
 * what AppointmentTypes.vue grabLink and GeneralSettings.vue build minus window.location.origin, so the
 * short-link service reuses the booking-urls rows they made. Throws on a blank siteID.
 * @param {{siteID:string, aptTypeId?:string|null}} args
 * @returns {string}
 */
function buildBookingRelativeUrl(args) {
  const o = args || {};
  if (isBlankString(o.siteID)) throw new Error('buildBookingRelativeUrl needs a siteID');
  let url = BOOKING_PAGE_PATH + '?siteID=' + encodeURIComponent(o.siteID);
  if (!isBlankString(o.aptTypeId)) url += '&apt=' + encodeURIComponent(o.aptTypeId);
  return url;
}

/**
 * The appointment types a recall message may open, from a site's parsed ONLINE_SCHEDULING_SETTINGS (an object, or its
 * JSON string; anything unparseable or shapeless gives []). A type counts when it has a non-blank string id, is not
 * hidden (hideService !== true) and is offered to existing patients (availableFor absent, or
 * availableFor.existingPatients !== false) - a recall goes to existing patients.
 * @param {*} onlineSchedulingSettings
 * @returns {Array<{id:string, name:string}>}
 */
function bookableRecallAppointmentTypes(onlineSchedulingSettings) {
  let settings = onlineSchedulingSettings;
  if (typeof settings === 'string') {
    try { settings = JSON.parse(settings); } catch (e) { return []; }
  }
  if (settings === null || typeof settings !== 'object' || !Array.isArray(settings.appointmentTypes)) return [];
  const out = [];
  settings.appointmentTypes.forEach(function (type) {
    if (type === null || typeof type !== 'object' || Array.isArray(type)) return;
    if (isBlankString(type.id)) return;
    if (type.hideService === true) return;
    const availableFor = type.availableFor;
    if (availableFor !== null && typeof availableFor === 'object' && availableFor.existingPatients === false) return;
    out.push({ id: type.id, name: typeof type.name === 'string' ? type.name : '' });
  });
  return out;
}

/**
 * The send-time decision for one inline recall message (plan 3.3 - teamio-utility applies it):
 *   - no token in `text`                          -> { action: 'none' }   (nothing to do, no extra queries)
 *   - `paidFor` is not 'TRUE' (trimmed, any case) -> { action: 'phone' }  (office has no online scheduling: the caller
 *                                                    swaps the token for the office phone and logs a warning)
 *   - otherwise                                   -> { action: 'link', relativeUrl, aptTypeId, fellBack }
 *       * bookingLink.aptTypeId is bookable at this site -> that page, fellBack false
 *       * it is set but not bookable (type deleted / hidden / new-patients only) -> the general booking page,
 *         aptTypeId null, fellBack true (the caller logs it)
 *       * no aptTypeId (or a blank one)               -> the general booking page, aptTypeId null, fellBack false
 * Throws (from buildBookingRelativeUrl) on a blank siteID when a link is wanted.
 * @param {{text:*, paidFor:*, onlineSchedulingSettings:*, bookingLink?:{aptTypeId?:*}|null, siteID:string}} input
 * @returns {{action:'none'}|{action:'phone'}|{action:'link', relativeUrl:string, aptTypeId:string|null, fellBack:boolean}}
 */
function resolveBookingLinkPlan(input) {
  const o = input || {};
  if (!hasBookingLinkToken(o.text)) return { action: 'none' };
  if (typeof o.paidFor !== 'string' || o.paidFor.trim().toUpperCase() !== 'TRUE') return { action: 'phone' };
  const wanted = o.bookingLink !== null && typeof o.bookingLink === 'object' ? o.bookingLink.aptTypeId : null;
  if (wanted === undefined || wanted === null || (typeof wanted === 'string' && wanted.trim() === '')) {
    return { action: 'link', relativeUrl: buildBookingRelativeUrl({ siteID: o.siteID }), aptTypeId: null, fellBack: false };
  }
  const bookable = bookableRecallAppointmentTypes(o.onlineSchedulingSettings).some(function (type) { return type.id === wanted; });
  if (bookable) {
    return { action: 'link', relativeUrl: buildBookingRelativeUrl({ siteID: o.siteID, aptTypeId: wanted }), aptTypeId: wanted, fellBack: false };
  }
  return { action: 'link', relativeUrl: buildBookingRelativeUrl({ siteID: o.siteID }), aptTypeId: null, fellBack: true };
}

/**
 * The one blank-message rule shared by the frontend Save, the backend save endpoints and the seeder.
 * For every slot in SLOT_DEFAULTS order whose value is an inline slot: the text must be a non-blank
 * string, and an email slot's subject must be too (whitespace-only counts as blank). Wiki slots, empty
 * slots and non-slot keys are ignored. An email slot with both blank reports the subject first (the
 * order on screen). A non-object blob yields [].
 *
 * Online-scheduling link token ({{online_scheduling_link}}), two rules (a field holding a token is never blank, so on
 * the same field a token problem and INLINE_SLOT_BLANK never both appear):
 *   - NEVER in a subject: an email slot's `subject` (the two recall emails included) and a recall-type rule email's
 *     `subject` holding the token gets INLINE_BOOKING_LINK_IN_SUBJECT (field 'subject').
 *   - In a message body only in the four recall slots (slotAllowsBookingLink) and in recall-type rules: an inline slot
 *     that is NOT one of those four, holding the token in `text`, gets INLINE_BOOKING_LINK_NOT_ALLOWED (field 'text').
 * The Spanish fields get the same two checks, with the field named for what they are: `inline.textSpa` ->
 * INLINE_BOOKING_LINK_NOT_ALLOWED (field 'textSpa', non-recall slots only), `inline.subjectSpa` ->
 * INLINE_BOOKING_LINK_IN_SUBJECT (field 'subjectSpa', every email slot and rule email). A blank or absent Spanish
 * field is never a problem (Spanish is optional / machine-made). On screen order per slot: subject, text, subjectSpa,
 * textSpa.
 *
 * WP6: after the slots it also walks `blob.recallReminderTypeRules` (rules by index; sms then email). For each
 * rule template that is an inline rule template: INLINE_RULE_ID_MISMATCH when its id names another channel or
 * another rule, then INLINE_SLOT_BLANK for a blank subject (email) / blank text; INLINE_RULE_ID_DUPLICATE (once,
 * on the LATER rule, channel null) when two rules carrying inline templates share an id. Non-object rules are
 * skipped but keep their index. Rule problems carry ruleIndex (0-based), ruleId, channel, field ('subject' |
 * 'text' | 'subjectSpa' | 'id') and key 'recallReminderTypeRules'. A rule's text may hold the token (rules always
 * allow it in the body); a rule email's subject / subjectSpa may not (INLINE_BOOKING_LINK_IN_SUBJECT, after the
 * blank checks of the same field).
 * @param {*} blob the parsed APPT_REMINDER_SETTINGS object
 * @returns {Array<object>} slot problems {key, field, code, message} with field 'subject' | 'text' | 'subjectSpa' |
 *   'textSpa'; rule problems add ruleIndex / ruleId / channel
 */
function validateInlineSlots(blob) {
  const problems = [];
  if (blob === null || typeof blob !== 'object' || Array.isArray(blob)) return problems;
  SLOT_DEFAULTS.forEach(function (def) {
    const slot = blob[def.key];
    if (!isInlineSlot(slot)) return;
    const inline = slot.inline !== null && typeof slot.inline === 'object' ? slot.inline : {};
    const allowsLink = slotAllowsBookingLink(def.key);
    const isEmail = def.channel === 'email';
    const linkNotAllowed = function (field) {
      problems.push({
        key: def.key,
        field: field,
        code: 'INLINE_BOOKING_LINK_NOT_ALLOWED',
        message: def.label + ': the online scheduling link can only be used in recall messages.',
      });
    };
    const linkInSubject = function (field) {
      problems.push({
        key: def.key,
        field: field,
        code: 'INLINE_BOOKING_LINK_IN_SUBJECT',
        message: def.label + BOOKING_LINK_IN_SUBJECT_TAIL,
      });
    };
    if (isEmail && isBlankString(inline.subject)) {
      problems.push({
        key: def.key,
        field: 'subject',
        code: 'INLINE_SLOT_BLANK',
        message: def.label + ': the subject line is required.',
      });
    } else if (isEmail && hasBookingLinkToken(inline.subject)) {
      linkInSubject('subject');
    }
    if (isBlankString(inline.text)) {
      problems.push({
        key: def.key,
        field: 'text',
        code: 'INLINE_SLOT_BLANK',
        message: def.label + ': the message is required.',
      });
    } else if (!allowsLink && hasBookingLinkToken(inline.text)) {
      linkNotAllowed('text');
    }
    // The Spanish fields: optional, so never blank problems - only the token rules above.
    if (isEmail && hasBookingLinkToken(inline.subjectSpa)) linkInSubject('subjectSpa');
    if (!allowsLink && hasBookingLinkToken(inline.textSpa)) linkNotAllowed('textSpa');
  });
  validateInlineRules(blob, problems);
  return problems;
}

const RULE_TEMPLATE_CHANNELS = [['smsTemplate', 'sms'], ['emailTemplate', 'email']];

function validateInlineRules(blob, problems) {
  const rules = blob.recallReminderTypeRules;
  if (!Array.isArray(rules)) return;
  const seenIds = new Set();
  rules.forEach(function (rule, i) {
    if (rule === null || typeof rule !== 'object' || Array.isArray(rule)) return;
    const carried = RULE_TEMPLATE_CHANNELS.filter(function (pair) { return isInlineRuleTemplate(rule[pair[0]]); });
    if (carried.length === 0) return;
    const ruleId = typeof rule.id === 'string' ? rule.id : null;
    const where = 'Recall type rule ' + (i + 1);
    if (ruleId !== null) {
      if (seenIds.has(ruleId)) {
        problems.push({
          key: 'recallReminderTypeRules',
          ruleIndex: i,
          ruleId: ruleId,
          channel: null,
          field: 'id',
          code: 'INLINE_RULE_ID_DUPLICATE',
          message: where + ': another recall type rule has the same id.',
        });
      }
      seenIds.add(ruleId);
    }
    carried.forEach(function (pair) {
      const channel = pair[1];
      const tpl = rule[pair[0]];
      const label = where + ' – ' + (channel === 'email' ? 'email' : 'text message');
      const ref = ruleRefFromInlineId(tpl.WikiPageID);
      const base = { key: 'recallReminderTypeRules', ruleIndex: i, ruleId: ruleId, channel: channel };
      if (ref.channel !== channel || ref.ruleId !== ruleId) {
        problems.push(Object.assign({}, base, {
          field: 'id',
          code: 'INLINE_RULE_ID_MISMATCH',
          message: label + ': the plain-message id does not match this rule.',
        }));
      }
      const inline = tpl.inline !== null && typeof tpl.inline === 'object' ? tpl.inline : {};
      if (channel === 'email' && isBlankString(inline.subject)) {
        problems.push(Object.assign({}, base, {
          field: 'subject',
          code: 'INLINE_SLOT_BLANK',
          message: label + ': the subject line is required.',
        }));
      } else if (channel === 'email' && hasBookingLinkToken(inline.subject)) {
        problems.push(Object.assign({}, base, {
          field: 'subject',
          code: 'INLINE_BOOKING_LINK_IN_SUBJECT',
          message: label + BOOKING_LINK_IN_SUBJECT_TAIL,
        }));
      }
      if (isBlankString(inline.text)) {
        problems.push(Object.assign({}, base, {
          field: 'text',
          code: 'INLINE_SLOT_BLANK',
          message: label + ': the message is required.',
        }));
      }
      // A rule's body may hold the token (rules always allow it there); its Spanish subject may not.
      if (channel === 'email' && hasBookingLinkToken(inline.subjectSpa)) {
        problems.push(Object.assign({}, base, {
          field: 'subjectSpa',
          code: 'INLINE_BOOKING_LINK_IN_SUBJECT',
          message: label + BOOKING_LINK_IN_SUBJECT_TAIL,
        }));
      }
    });
  });
}
// === SHARED-BODY-END ===

export {
  INLINE_PREFIX,
  SLOT_DEFAULTS,
  inlineSlotId,
  isInlineSlotId,
  slotKeyFromInlineId,
  getSlotDefault,
  isInlineSlot,
  buildInlineSlot,
  validateInlineSlots,
  RULE_CHANNEL_SLOT,
  inlineRuleId,
  isInlineRuleId,
  ruleRefFromInlineId,
  getRuleDefault,
  isInlineRuleTemplate,
  buildInlineRuleTemplate,
  BOOKING_LINK_VARIABLE,
  BOOKING_LINK_TOKEN,
  slotAllowsBookingLink,
  inlineIdAllowsBookingLink,
  hasBookingLinkToken,
  substituteBookingLink,
  pickDefaultText,
  buildBookingRelativeUrl,
  bookableRecallAppointmentTypes,
  resolveBookingLinkPlan,
};
